import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Avatar,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  Create as CreateIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Grade as GradeIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Assignment as AssignmentIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
} from "@mui/icons-material";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import HtmlRenderer from "./HtmlRenderer";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import {
  useGetWritingCommentsQuery,
  useCreateWritingCommentMutation,
  useReplyToWritingCommentMutation,
  useDeleteWritingCommentMutation,
  useDeleteWritingCommentReplyMutation,
} from "../mockTestSlice";
import { useGetCurrentAccountQuery } from "../../user/loginSlice";

/* ---------------------------------------------------------------
   Brand Colors (Only white and red)
--------------------------------------------------------------- */
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";
const SUCCESS_GREEN = "#4caf50";
const SUCCESS_BG = "#e8f5e9";

/* ---------------------------------------------------------------
   OET Grade Helper
--------------------------------------------------------------- */
function getOetGrade(marks) {
  if (marks == null) return null;
  const score = Number(marks);
  if (score >= 450) return { grade: "A", color: SUCCESS_GREEN };
  if (score >= 400) return { grade: "B", color: "#2196f3" };
  if (score >= 350) return { grade: "C+", color: "#ff9800" };
  if (score >= 300) return { grade: "C", color: "#ff9800" };
  if (score >= 200) return { grade: "D", color: BRAND_PRIMARY };
  return { grade: "E", color: BRAND_DARK };
}

/* ---------------------------------------------------------------
   IELTS Band Score Helper
--------------------------------------------------------------- */
function getIeltsBandInfo(band) {
  if (band == null) return null;
  const score = Number(band);
  if (score >= 8) return { color: SUCCESS_GREEN, label: "Expert" };
  if (score >= 7) return { color: "#2196f3", label: "Very Good" };
  if (score >= 6) return { color: "#03a9f4", label: "Competent" };
  if (score >= 5) return { color: "#ff9800", label: "Modest" };
  if (score >= 4) return { color: "#ff5722", label: "Limited" };
  return { color: BRAND_DARK, label: "Basic" };
}

/* ---------------------------------------------------------------
   Image Viewer Modal
--------------------------------------------------------------- */
function ImageViewerModal({ open, onClose, imageUrl, imageName }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {imageName || "Submitted Image"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <img
          src={imageUrl}
          alt={imageName || "Writing submission"}
          style={{
            maxWidth: "100%",
            maxHeight: "75vh",
            objectFit: "contain",
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------
   Case Notes Section (Left Panel) - Matches WritingSection.js exactly
--------------------------------------------------------------- */
function CaseNotesSection({ caseNotes = [], onAddNote = null }) {
  if (!caseNotes.length) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: "#64748b" }}>
        <DescriptionIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
        <Typography variant="body2">No case notes available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: { xs: 1, sm: 2 },
          fontSize: { xs: "1rem", sm: "1.25rem" },
        }}
      >
        Case Notes
      </Typography>

      {caseNotes.map((note, idx) => (
        <Card
          key={note._id || idx}
          elevation={2}
          sx={{
            mb: { xs: 2, sm: 3 },
            p: { xs: 1.5, sm: 3 },
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
            background: "#fff",
          }}
        >
          <TextHighlighterWrapper onAddNote={onAddNote}>
            {note.title && (
              <HtmlRenderer
                content={note.title}
                component="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, sm: 2 },
                  color: "#9D2235",
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              />
            )}

            {(!note.subSections || note.subSections.length === 0) &&
              note.content && (
                <HtmlRenderer
                  content={note.content}
                  component="div"
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    lineHeight: { xs: 1.6, sm: 1.8 },
                    whiteSpace: "pre-line",
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  }}
                />
              )}

            {note.subSections?.length > 0 && (
              <Box sx={{ ml: 2, mb: 1 }}>
                {note.subSections.length > 1 ? (
                  <Box component="ul" sx={{ listStyle: "disc", pl: 5 }}>
                    {note.subSections.map((sub, sidx) => (
                      <Box component="li" key={sub._id || sidx} sx={{ mb: 1 }}>
                        {sub.label && (
                          <HtmlRenderer
                            content={`${sub.label}${sub.label ? ": " : ""}`}
                            component="span"
                            sx={{
                              fontWeight: 500,
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          />
                        )}
                        {sub.content && (
                          <HtmlRenderer
                            content={sub.content}
                            component="span"
                            sx={{
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box>
                    {note.subSections[0].label && (
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      >
                        {note.subSections[0].label}
                        {note.subSections[0].label ? ": " : ""}
                      </Typography>
                    )}
                    {note.subSections[0].content && (
                      <HtmlRenderer
                        content={note.subSections[0].content}
                        component="div"
                        sx={{
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            )}
          </TextHighlighterWrapper>
        </Card>
      ))}
    </Box>
  );
}

/* ---------------------------------------------------------------
   Writing Task & Submission Section (Right Panel) with Comments
--------------------------------------------------------------- */
function WritingTaskSection({
  writingPrompt,
  instructions,
  submittedText,
  submittedImages,
  isPaperBased,
  evaluationPdf,
  marks,
  grade,
  adminComments,
  createdAt,
  isEvaluated,
  checkingStatus,
  writingAnswerId,
  isIelts = false,
  onAddNote = null,
  taskNumber = null,
}) {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Comment State
  const [activeCommentSection, setActiveCommentSection] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    text: "",
    severity: "success",
  });

  // Account data
  const { data: accountData } = useGetCurrentAccountQuery();
  const isLinkedWithInstitution =
    accountData?.data?.data?.account?.isLinkedWithInstitution ?? false;

  // Comment API Hooks (WritingTaskSection)
  const [createComment, { isLoading: isCreating }] =
    useCreateWritingCommentMutation();
  const [replyToComment, { isLoading: isReplying }] =
    useReplyToWritingCommentMutation();
  const [deleteComment] = useDeleteWritingCommentMutation();
  const [deleteReply] = useDeleteWritingCommentReplyMutation();

  // Fetch comments
  const {
    data: commentsData,
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useGetWritingCommentsQuery(
    { writingAnswerId },
    { skip: !writingAnswerId },
  );

  // Debug logging
  useEffect(() => {
    console.log("WritingTaskSection - writingAnswerId:", writingAnswerId);
    console.log("WritingTaskSection - commentsData:", commentsData);
  }, [writingAnswerId, commentsData]);

  // Handle comment submission
  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    try {
      await createComment({
        writingAnswerId,
        commentText: newComment.trim(),
      }).unwrap();

      setNewComment("");
      refetchComments();
      setToast({
        open: true,
        text: "Comment sent successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error creating comment:", error);
      setToast({
        open: true,
        text: "Failed to send comment",
        severity: "error",
      });
    }
  };

  // Handle reply submission
  const handleSubmitReply = async (commentId) => {
    if (!replyText.trim()) return;

    try {
      await replyToComment({
        writingAnswerId,
        commentId,
        commentText: replyText.trim(),
      }).unwrap();

      setReplyText("");
      setReplyingTo(null);
      refetchComments();
      setToast({
        open: true,
        text: "Reply sent successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error replying to comment:", error);
      setToast({ open: true, text: "Failed to send reply", severity: "error" });
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    setDeleteItem({ type: "comment", commentId });
    setDeleteConfirmOpen(true);
  };

  // Handle delete reply
  const handleDeleteReply = async (commentId, replyId) => {
    setDeleteItem({ type: "reply", commentId, replyId });
    setDeleteConfirmOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deleteItem) return;

    try {
      if (deleteItem.type === "comment") {
        await deleteComment({
          writingAnswerId,
          commentId: deleteItem.commentId,
        }).unwrap();
        setToast({
          open: true,
          text: "Comment deleted successfully",
          severity: "success",
        });
      } else if (deleteItem.type === "reply") {
        await deleteReply({
          writingAnswerId,
          commentId: deleteItem.commentId,
          replyId: deleteItem.replyId,
        }).unwrap();
        setToast({
          open: true,
          text: "Reply deleted successfully",
          severity: "success",
        });
      }
      refetchComments();
    } catch (error) {
      console.error("Error deleting:", error);
      setToast({ open: true, text: "Failed to delete", severity: "error" });
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
    }
  };

  const gradeInfo =
    marks !== null && marks !== undefined
      ? isIelts
        ? getIeltsBandInfo(marks)
        : getOetGrade(marks)
      : null;

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setImageModalOpen(true);
  };

  const wordCount = submittedText
    ? submittedText.trim().split(/\s+/).filter(Boolean).length
    : 0;

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      {/* Writing Task Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AssignmentIcon sx={{ color: BRAND_PRIMARY }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Writing Task {taskNumber || 1} Submission
            </Typography>
          </Box>
        </Box>

        <CardContent>


          {/* Writing Prompt */}
          {writingPrompt && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "#fff",
                borderRadius: 2,
              }}
            >
              <TextHighlighterWrapper onAddNote={onAddNote}>
                <HtmlRenderer
                  content={writingPrompt}
                  component="div"
                  sx={{
                    lineHeight: 1.7,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#374151",
                  }}
                />
              </TextHighlighterWrapper>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Student Submission Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: BRAND_BG,
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {submittedImages && submittedImages.length > 0 ? (
              <ImageIcon sx={{ color: BRAND_PRIMARY }} />
            ) : (
              <CreateIcon sx={{ color: BRAND_PRIMARY }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              {submittedImages && submittedImages.length > 0
                ? "Paper-Based Submission"
                : "Student Submission"}
            </Typography>
          </Box>

          {createdAt && (
            <Chip
              icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
              label={new Date(createdAt).toLocaleString()}
              size="small"
              variant="outlined"
              sx={{ borderColor: BRAND_PRIMARY, color: BRAND_PRIMARY }}
            />
          )}
        </Box>

        <CardContent>
          {/* Show Images if they exist (Paper-Based submission) */}
          {submittedImages && submittedImages.length > 0 ? (
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#64748b",
                  fontSize: "0.875rem",
                }}
              >
                Uploaded Images ({submittedImages.length})
              </Typography>
              <Grid container spacing={2}>
                {submittedImages.map((image, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "scale(1.02)",
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => handleImageClick(image)}
                    >
                      <Box sx={{ position: "relative" }}>
                        <img
                          src={image.imageUrl}
                          alt={`Writing submission ${index + 1}`}
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.6)",
                            borderRadius: 1,
                            p: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <ZoomInIcon sx={{ color: "#fff", fontSize: 16 }} />
                          <Typography sx={{ color: "#fff", fontSize: 11 }}>
                            Click to view
                          </Typography>
                        </Box>
                      </Box>
                      {image.originalName && (
                        <Box sx={{ p: 1.5, bgcolor: "#f8fafc" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {image.originalName}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : submittedText ? (
            /* Computer-Based: Show Text (also fallback if no images but has text) */
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  Written Response
                </Typography>
                <Chip
                  label={`${wordCount} words`}
                  size="small"
                  sx={{
                    bgcolor:
                      wordCount >= 180 && wordCount <= 200
                        ? SUCCESS_BG
                        : BRAND_BG,
                    color:
                      wordCount >= 180 && wordCount <= 200
                        ? SUCCESS_GREEN
                        : BRAND_PRIMARY,
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  bgcolor: "#fff",
                  borderRadius: 2,
                  minHeight: 200,
                }}
              >
                <TextHighlighterWrapper onAddNote={onAddNote}>
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.8,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      color: "#374151",
                      fontFamily: "'Georgia', serif",
                    }}
                  >
                    {submittedText}
                  </Typography>
                </TextHighlighterWrapper>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4, color: "#64748b" }}>
              <DescriptionIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
              <Typography variant="body2">No submission found</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Evaluation Card */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: isEvaluated ? SUCCESS_BG : "#f8fafc",
            borderBottom: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isEvaluated ? (
              <CheckCircleIcon sx={{ color: SUCCESS_GREEN }} />
            ) : (
              <ScheduleIcon sx={{ color: "#64748b" }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: isEvaluated ? SUCCESS_GREEN : "#64748b",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              {isEvaluated ? "Assessor Evaluation" : "Pending Evaluation"}
            </Typography>
          </Box>

          {isEvaluated &&
            marks !== null &&
            marks !== undefined &&
            marks > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: gradeInfo?.color || BRAND_PRIMARY,
                    }}
                  >
                    {marks}
                  </Typography>
                </Box>
                <Chip
                  icon={<GradeIcon sx={{ fontSize: 16 }} />}
                  label={`Grade ${grade || gradeInfo?.grade}`}
                  sx={{
                    bgcolor: `${gradeInfo?.color || BRAND_PRIMARY}20`,
                    color: gradeInfo?.color || BRAND_PRIMARY,
                    fontWeight: 600,
                  }}
                />
              </Box>
            )}
        </Box>

        <CardContent>
          {isEvaluated ? (
            <Box>
              {/* Assessor Comments */}
              {adminComments && (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <CommentIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "0.95rem",
                      }}
                    >
                      Examiner's Comments
                    </Typography>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      borderLeft: `3px solid ${BRAND_PRIMARY}`,
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.7,
                        color: "#374151",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      {adminComments}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* PDF Viewer with Comment Toggle */}
              {evaluationPdf && (
                <Box sx={{ mb: 3 }}>
                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={evaluationPdf}
                      download
                      sx={{
                        borderColor: BRAND_PRIMARY,
                        color: BRAND_PRIMARY,
                        "&:hover": {
                          borderColor: BRAND_DARK,
                          bgcolor: BRAND_BG,
                        },
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Download PDF
                    </Button>
                    {writingAnswerId && !isLinkedWithInstitution && (
                      <Button
                        variant={
                          activeCommentSection ? "outlined" : "contained"
                        }
                        size="medium"
                        onClick={() =>
                          setActiveCommentSection(!activeCommentSection)
                        }
                        startIcon={
                          activeCommentSection ? <CloseIcon /> : <SendIcon />
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          px: 4,
                          py: 1,
                          borderRadius: 3,
                          minWidth: 180,
                          bgcolor: activeCommentSection
                            ? "transparent"
                            : BRAND_PRIMARY,
                          borderColor: BRAND_PRIMARY,
                          borderWidth: 2,
                          color: activeCommentSection ? BRAND_PRIMARY : "white",
                          "&:hover": {
                            bgcolor: activeCommentSection
                              ? BRAND_BG
                              : BRAND_DARK,
                            borderColor: BRAND_DARK,
                            borderWidth: 2,
                            transform: "translateY(-1px)",
                            boxShadow: `0 8px 25px rgba(157, 34, 53, 0.25)`,
                          },
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: activeCommentSection
                            ? `0 2px 8px rgba(157, 34, 53, 0.15)`
                            : `0 4px 14px rgba(157, 34, 53, 0.4)`,
                        }}
                      >
                        {activeCommentSection
                          ? "Exit Comment Mode"
                          : "Start Comment"}
                      </Button>
                    )}
                  </Box>

                  {/* Split Screen Layout when comments active */}
                  {activeCommentSection && writingAnswerId ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                        gap: 3,
                        minHeight: 700,
                      }}
                    >
                      {/* Left Side - PDF Viewer */}
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: "#f8fafc",
                          borderRadius: 3,
                          border: "2px solid #e2e8f0",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            background:
                              "linear-gradient(135deg, #334155 0%, #1e293b 100%)",
                            color: "white",
                            p: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: 18 }}>📄</Typography>
                            <Typography
                              sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                            >
                              Writing Feedback PDF
                            </Typography>
                          </Box>
                          <Chip
                            label="Live View"
                            size="small"
                            sx={{
                              bgcolor: "#475569",
                              color: "white",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                        <Box sx={{ height: 650, width: "100%" }}>
                          <iframe
                            src={`${evaluationPdf}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              background: "white",
                            }}
                            title="Writing Feedback PDF"
                          />
                        </Box>
                      </Paper>

                      {/* Right Side - Chat Interface */}
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: "white",
                          borderRadius: 3,
                          border: "1px solid #e2e8f0",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Chat Header */}
                        <Box
                          sx={{
                            background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`,
                            p: 2,
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: "rgba(255,255,255,0.2)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CommentIcon sx={{ color: "white" }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                sx={{
                                  color: "white",
                                  fontWeight: 700,
                                  fontSize: "1.125rem",
                                }}
                              >
                                Assessment Discussion
                              </Typography>
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.8)",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {commentsData?.comments?.length || 0} messages
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  bgcolor: "#4ade80",
                                  borderRadius: "50%",
                                  animation:
                                    "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                                  "@keyframes pulse": {
                                    "0%, 100%": { opacity: 1 },
                                    "50%": { opacity: 0.5 },
                                  },
                                }}
                              />
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.9)",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                }}
                              >
                                Active
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Messages Area */}
                        <Box
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            p: 2,
                            maxHeight: 500,
                            bgcolor: "#f8fafc",
                          }}
                        >
                          {commentsLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 8,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: "#64748b",
                                }}
                              >
                                <CircularProgress
                                  size={16}
                                  sx={{ color: BRAND_PRIMARY }}
                                />
                                <Typography sx={{ fontSize: "0.875rem" }}>
                                  Loading messages...
                                </Typography>
                              </Box>
                            </Box>
                          ) : commentsData?.comments?.length > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {commentsData.comments.map((comment, index) => (
                                <Box key={comment._id || index}>
                                  {/* Main Comment Message */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "start",
                                      gap: 1.5,
                                      justifyContent:
                                        comment.createdByModel === "student"
                                          ? "flex-start"
                                          : "flex-end",
                                    }}
                                  >
                                    {comment.createdByModel === "student" && (
                                      <Avatar
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`,
                                          boxShadow:
                                            "0 4px 12px rgba(157, 34, 53, 0.3)",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {comment.createdBy?.fullName
                                            ?.charAt(0)
                                            ?.toUpperCase() || "S"}
                                        </Typography>
                                      </Avatar>
                                    )}

                                    <Box
                                      sx={{
                                        maxWidth: "75%",
                                        order:
                                          comment.createdByModel === "student"
                                            ? 2
                                            : 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          borderRadius: 4,
                                          px: 2,
                                          py: 1.5,
                                          boxShadow:
                                            "0 2px 8px rgba(0,0,0,0.1)",
                                          background:
                                            comment.createdByModel === "student"
                                              ? `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`
                                              : "white",
                                          color:
                                            comment.createdByModel === "student"
                                              ? "white"
                                              : "#374151",
                                          borderBottomLeftRadius:
                                            comment.createdByModel === "student"
                                              ? 4
                                              : 16,
                                          borderBottomRightRadius:
                                            comment.createdByModel === "student"
                                              ? 16
                                              : 4,
                                          border:
                                            comment.createdByModel === "student"
                                              ? "none"
                                              : "2px solid #e2e8f0",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {comment.createdBy?.fullName ||
                                              (comment.createdByModel ===
                                              "student"
                                                ? "Student"
                                                : "Assessor")}
                                          </Typography>
                                          <Chip
                                            label={
                                              comment.createdByModel ===
                                              "student"
                                                ? "Student"
                                                : "Assessor"
                                            }
                                            size="small"
                                            sx={{
                                              height: 18,
                                              fontSize: "0.65rem",
                                              fontWeight: 600,
                                              bgcolor:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.2)"
                                                  : "#dcfce7",
                                              color:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.8)"
                                                  : "#16a34a",
                                            }}
                                          />
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontSize: "0.875rem",
                                            lineHeight: 1.6,
                                            whiteSpace: "pre-wrap",
                                          }}
                                        >
                                          {comment.commentText}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mt: 1.5,
                                            pt: 1,
                                            borderTop: `1px solid ${
                                              comment.createdByModel ===
                                              "student"
                                                ? "rgba(255,255,255,0.2)"
                                                : "#e2e8f0"
                                            }`,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "0.7rem",
                                              color:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.7)"
                                                  : "#64748b",
                                            }}
                                          >
                                            {new Date(
                                              comment.createdAt,
                                            ).toLocaleString("en-US", {
                                              month: "short",
                                              day: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <Button
                                              size="small"
                                              onClick={() => {
                                                setReplyingTo(
                                                  replyingTo === comment._id
                                                    ? null
                                                    : comment._id,
                                                );
                                                setReplyText("");
                                              }}
                                              sx={{
                                                fontSize: "0.7rem",
                                                fontWeight: 600,
                                                textTransform: "none",
                                                minWidth: "auto",
                                                p: 0.5,
                                                color:
                                                  comment.createdByModel ===
                                                  "student"
                                                    ? "rgba(255,255,255,0.8)"
                                                    : BRAND_PRIMARY,
                                                "&:hover": {
                                                  color:
                                                    comment.createdByModel ===
                                                    "student"
                                                      ? "white"
                                                      : BRAND_DARK,
                                                },
                                              }}
                                            >
                                              Reply
                                            </Button>
                                            {comment.createdByModel ===
                                              "student" && (
                                              <Button
                                                size="small"
                                                onClick={() =>
                                                  handleDeleteComment(
                                                    comment._id,
                                                  )
                                                }
                                                sx={{
                                                  fontSize: "0.7rem",
                                                  fontWeight: 600,
                                                  textTransform: "none",
                                                  minWidth: "auto",
                                                  p: 0.5,
                                                  color: "#fca5a5",
                                                  "&:hover": {
                                                    color: "#ef4444",
                                                  },
                                                }}
                                              >
                                                Delete
                                              </Button>
                                            )}
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {comment.createdByModel !== "student" && (
                                      <Avatar
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          background:
                                            "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                          boxShadow:
                                            "0 4px 12px rgba(16, 185, 129, 0.3)",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {comment.createdBy?.fullName
                                            ?.charAt(0)
                                            ?.toUpperCase() || "A"}
                                        </Typography>
                                      </Avatar>
                                    )}
                                  </Box>

                                  {/* Reply Input */}
                                  {replyingTo === comment._id && (
                                    <Box
                                      sx={{
                                        ml: 6,
                                        mt: 1.5,
                                        bgcolor: "white",
                                        borderRadius: 2,
                                        p: 2,
                                        border: "1px solid #e2e8f0",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          mb: 1,
                                        }}
                                      >
                                        <ReplyIcon
                                          sx={{
                                            color: BRAND_PRIMARY,
                                            fontSize: 16,
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: "0.875rem",
                                            color: "#64748b",
                                            fontWeight: 600,
                                          }}
                                        >
                                          Replying to comment
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setReplyingTo(null);
                                            setReplyText("");
                                          }}
                                          sx={{ ml: "auto", p: 0.5 }}
                                        >
                                          <CloseIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "#64748b",
                                            }}
                                          />
                                        </IconButton>
                                      </Box>
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          value={replyText}
                                          onChange={(e) =>
                                            setReplyText(e.target.value)
                                          }
                                          placeholder="Write a reply..."
                                          onKeyPress={(e) => {
                                            if (
                                              e.key === "Enter" &&
                                              !e.shiftKey
                                            ) {
                                              e.preventDefault();
                                              handleSubmitReply(comment._id);
                                            }
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 2,
                                            },
                                          }}
                                        />
                                        <Button
                                          variant="contained"
                                          onClick={() =>
                                            handleSubmitReply(comment._id)
                                          }
                                          disabled={
                                            !replyText.trim() || isReplying
                                          }
                                          sx={{
                                            bgcolor: BRAND_PRIMARY,
                                            "&:hover": { bgcolor: BRAND_DARK },
                                            minWidth: "auto",
                                            px: 2,
                                          }}
                                        >
                                          {isReplying ? (
                                            <CircularProgress
                                              size={16}
                                              color="inherit"
                                            />
                                          ) : (
                                            "Send"
                                          )}
                                        </Button>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Replies */}
                                  {comment.replies &&
                                    comment.replies.length > 0 && (
                                      <Box
                                        sx={{
                                          ml: 6,
                                          mt: 1,
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1,
                                        }}
                                      >
                                        {comment.replies.map(
                                          (reply, replyIndex) => (
                                            <Box
                                              key={reply._id || replyIndex}
                                              sx={{
                                                display: "flex",
                                                alignItems: "start",
                                                gap: 1,
                                                justifyContent:
                                                  reply.repliedByModel ===
                                                  "student"
                                                    ? "flex-start"
                                                    : "flex-end",
                                              }}
                                            >
                                              {reply.repliedByModel ===
                                                "student" && (
                                                <Avatar
                                                  sx={{
                                                    width: 32,
                                                    height: 32,
                                                    background: `linear-gradient(135deg, ${BRAND_PRIMARY}CC 0%, ${BRAND_DARK}CC 100%)`,
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontWeight: 700,
                                                      fontSize: "0.75rem",
                                                    }}
                                                  >
                                                    {reply.repliedBy?.fullName
                                                      ?.charAt(0)
                                                      ?.toUpperCase() || "S"}
                                                  </Typography>
                                                </Avatar>
                                              )}

                                              <Box
                                                sx={{
                                                  maxWidth: "70%",
                                                  borderRadius: 3,
                                                  px: 1.5,
                                                  py: 1,
                                                  boxShadow:
                                                    "0 1px 4px rgba(0,0,0,0.08)",
                                                  background:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? `linear-gradient(135deg, ${BRAND_PRIMARY}E6 0%, ${BRAND_DARK}E6 100%)`
                                                      : "#f1f5f9",
                                                  color:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? "white"
                                                      : "#374151",
                                                  borderBottomLeftRadius:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? 2
                                                      : 12,
                                                  borderBottomRightRadius:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? 12
                                                      : 2,
                                                  border:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? "none"
                                                      : "1px solid #e2e8f0",
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    mb: 0.5,
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: "0.75rem",
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    {reply.repliedBy
                                                      ?.fullName ||
                                                      (reply.repliedByModel ===
                                                      "student"
                                                        ? "Student"
                                                        : "Assessor")}
                                                  </Typography>
                                                </Box>
                                                <Typography
                                                  sx={{
                                                    fontSize: "0.75rem",
                                                    lineHeight: 1.5,
                                                    whiteSpace: "pre-wrap",
                                                  }}
                                                >
                                                  {reply.commentText}
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                      "space-between",
                                                    mt: 0.5,
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: "0.65rem",
                                                      color:
                                                        reply.repliedByModel ===
                                                        "student"
                                                          ? "rgba(255,255,255,0.7)"
                                                          : "#64748b",
                                                    }}
                                                  >
                                                    {new Date(
                                                      reply.createdAt,
                                                    ).toLocaleString("en-US", {
                                                      month: "short",
                                                      day: "numeric",
                                                      hour: "2-digit",
                                                      minute: "2-digit",
                                                    })}
                                                  </Typography>
                                                  {reply.repliedByModel ===
                                                    "student" && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                        handleDeleteReply(
                                                          comment._id,
                                                          reply._id,
                                                        )
                                                      }
                                                      sx={{
                                                        p: 0.25,
                                                        color: "#fca5a5",
                                                        "&:hover": {
                                                          color: "#ef4444",
                                                        },
                                                      }}
                                                    >
                                                      <DeleteIcon
                                                        sx={{ fontSize: 14 }}
                                                      />
                                                    </IconButton>
                                                  )}
                                                </Box>
                                              </Box>

                                              {reply.repliedByModel !==
                                                "student" && (
                                                <Avatar
                                                  sx={{
                                                    width: 32,
                                                    height: 32,
                                                    background:
                                                      "linear-gradient(135deg, #10b981CC 0%, #059669CC 100%)",
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontWeight: 700,
                                                      fontSize: "0.75rem",
                                                    }}
                                                  >
                                                    {reply.repliedBy?.fullName
                                                      ?.charAt(0)
                                                      ?.toUpperCase() || "A"}
                                                  </Typography>
                                                </Avatar>
                                              )}
                                            </Box>
                                          ),
                                        )}
                                      </Box>
                                    )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 8,
                                textAlign: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  bgcolor: "#f1f5f9",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mb: 2,
                                }}
                              >
                                <CommentIcon
                                  sx={{ fontSize: 32, color: "#cbd5e1" }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: "#64748b",
                                  mb: 0.5,
                                }}
                              >
                                No messages yet
                              </Typography>
                              <Typography
                                sx={{ fontSize: "0.875rem", color: "#94a3b8" }}
                              >
                                Start a conversation with your assessor
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Message Input */}
                        <Box
                          sx={{
                            p: 2,
                            borderTop: "1px solid #e2e8f0",
                            bgcolor: "white",
                            borderBottomLeftRadius: 12,
                            borderBottomRightRadius: 12,
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 1.5 }}>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              maxRows={3}
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Type your message..."
                              onKeyPress={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSubmitComment();
                                }
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 3,
                                  bgcolor: "#f8fafc",
                                },
                              }}
                            />
                            <Button
                              variant="contained"
                              onClick={handleSubmitComment}
                              disabled={!newComment.trim() || isCreating}
                              sx={{
                                bgcolor: BRAND_PRIMARY,
                                "&:hover": { bgcolor: BRAND_DARK },
                                minWidth: "auto",
                                px: 3,
                                borderRadius: 3,
                              }}
                            >
                              {isCreating ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <SendIcon />
                              )}
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  ) : (
                    /* Normal PDF View */
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 600,
                          position: "relative",
                          bgcolor: "#ffffff",
                        }}
                      >
                        <iframe
                          src={`${evaluationPdf}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                          }}
                          title="Writing Feedback PDF"
                        />
                        {/* Fallback content */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f8fafc",
                            zIndex: -1,
                          }}
                        >
                          <Box sx={{ textAlign: "center", p: 3 }}>
                            <PdfIcon
                              sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }}
                            />
                            <Typography sx={{ color: "#64748b", mb: 1.5 }}>
                              PDF preview not available in your browser
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#94a3b8" }}
                            >
                              Click "Download PDF" button above to open the PDF
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}

              {!adminComments && !evaluationPdf && (
                <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                  Your writing has been evaluated. Score and grade are shown
                  above.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: BRAND_BG,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <ScheduleIcon sx={{ fontSize: 32, color: BRAND_LIGHT }} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: BRAND_PRIMARY, mb: 1 }}
              >
                Evaluation in Progress
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                Your writing will be evaluated within 24 hours. Please check
                back later for your results.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        imageUrl={selectedImage?.imageUrl}
        imageName={selectedImage?.originalName}
      />

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.text}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeleteItem(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#1e293b",
            fontSize: "1.125rem",
            pb: 1,
          }}
        >
          {deleteItem?.type === "comment" ? "Delete Comment?" : "Delete Reply?"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#64748b", mb: 2 }}>
            {deleteItem?.type === "comment"
              ? "This comment and all its replies will be permanently deleted. This action cannot be undone."
              : "This reply will be permanently deleted. This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteItem(null);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#64748b",
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: "#ef4444",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#dc2626" },
            }}
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ---------------------------------------------------------------
   Writing Evaluation Section (for multi-task view) with Comments
--------------------------------------------------------------- */
function WritingEvaluationSection({
  evaluationPdf,
  marks,
  grade,
  adminComments,
  isEvaluated,
  checkingStatus,
  writingAnswerId,
  isIelts = false,
}) {
  const gradeInfo =
    marks !== null && marks !== undefined
      ? isIelts
        ? getIeltsBandInfo(marks)
        : getOetGrade(marks)
      : null;

  // Comment State
  const [activeCommentSection, setActiveCommentSection] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    text: "",
    severity: "success",
  });

  // Account data
  const { data: accountData } = useGetCurrentAccountQuery();
  const isLinkedWithInstitution =
    accountData?.data?.data?.account?.isLinkedWithInstitution ?? false;

  // Comment API Hooks (WritingEvaluationSection)
  const [createComment, { isLoading: isCreating }] =
    useCreateWritingCommentMutation();
  const [replyToComment, { isLoading: isReplying }] =
    useReplyToWritingCommentMutation();
  const [deleteComment] = useDeleteWritingCommentMutation();
  const [deleteReply] = useDeleteWritingCommentReplyMutation();

  // Fetch comments
  const {
    data: commentsData,
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useGetWritingCommentsQuery(
    { writingAnswerId },
    { skip: !writingAnswerId },
  );

  // Debug logging
  useEffect(() => {
    console.log("WritingEvaluationSection - writingAnswerId:", writingAnswerId);
    console.log("WritingEvaluationSection - commentsData:", commentsData);
  }, [writingAnswerId, commentsData]);

  // Handle comment submission
  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    try {
      await createComment({
        writingAnswerId,
        commentText: newComment.trim(),
      }).unwrap();

      setNewComment("");
      refetchComments();
      setToast({
        open: true,
        text: "Comment sent successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error creating comment:", error);
      setToast({
        open: true,
        text: "Failed to send comment",
        severity: "error",
      });
    }
  };

  // Handle reply submission
  const handleSubmitReply = async (commentId) => {
    if (!replyText.trim()) return;

    try {
      await replyToComment({
        writingAnswerId,
        commentId,
        commentText: replyText.trim(),
      }).unwrap();

      setReplyText("");
      setReplyingTo(null);
      refetchComments();
      setToast({
        open: true,
        text: "Reply sent successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error replying to comment:", error);
      setToast({ open: true, text: "Failed to send reply", severity: "error" });
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    setDeleteItem({ type: "comment", commentId });
    setDeleteConfirmOpen(true);
  };

  // Handle delete reply
  const handleDeleteReply = async (commentId, replyId) => {
    setDeleteItem({ type: "reply", commentId, replyId });
    setDeleteConfirmOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deleteItem) return;

    try {
      if (deleteItem.type === "comment") {
        await deleteComment({
          writingAnswerId,
          commentId: deleteItem.commentId,
        }).unwrap();
        setToast({
          open: true,
          text: "Comment deleted successfully",
          severity: "success",
        });
      } else if (deleteItem.type === "reply") {
        await deleteReply({
          writingAnswerId,
          commentId: deleteItem.commentId,
          replyId: deleteItem.replyId,
        }).unwrap();
        setToast({
          open: true,
          text: "Reply deleted successfully",
          severity: "success",
        });
      }
      refetchComments();
    } catch (error) {
      console.error("Error deleting:", error);
      setToast({ open: true, text: "Failed to delete", severity: "error" });
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
    }
  };

  // Don't show evaluation section until test is evaluated
  if (!isEvaluated) {
    return null;
  }

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
          borderRadius: 2,
          mt: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: isEvaluated ? SUCCESS_BG : "#f8fafc",
            borderBottom: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isEvaluated ? (
              <CheckCircleIcon sx={{ color: SUCCESS_GREEN }} />
            ) : (
              <ScheduleIcon sx={{ color: "#64748b" }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: isEvaluated ? SUCCESS_GREEN : "#64748b",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              {isEvaluated ? "Overall Evaluation" : "Pending Evaluation"}
            </Typography>
          </Box>

          {isEvaluated && marks !== null && marks !== undefined && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: gradeInfo?.color || BRAND_PRIMARY,
                }}
              >
                {isIelts ? `Band ${marks}` : marks}
              </Typography>
              {(grade || gradeInfo?.grade || gradeInfo?.label) && (
                <Chip
                  icon={<GradeIcon sx={{ fontSize: 16 }} />}
                  label={
                    isIelts
                      ? gradeInfo?.label || `Band ${marks}`
                      : `Grade ${grade || gradeInfo?.grade}`
                  }
                  sx={{
                    bgcolor: `${gradeInfo?.color || BRAND_PRIMARY}20`,
                    color: gradeInfo?.color || BRAND_PRIMARY,
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        <CardContent>
          {isEvaluated ? (
            <Box>
              {adminComments && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CommentIcon sx={{ color: BRAND_PRIMARY, fontSize: 18 }} />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "0.9rem",
                      }}
                    >
                      Examiner's Comments
                    </Typography>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      borderLeft: `3px solid ${BRAND_PRIMARY}`,
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.6,
                        color: "#374151",
                        fontSize: "0.9rem",
                      }}
                    >
                      {adminComments}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* PDF Viewer with Comment Toggle */}
              {evaluationPdf && (
                <Box sx={{ mb: 3 }}>
                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={evaluationPdf}
                      download
                      sx={{
                        borderColor: BRAND_PRIMARY,
                        color: BRAND_PRIMARY,
                        "&:hover": {
                          borderColor: BRAND_DARK,
                          bgcolor: BRAND_BG,
                        },
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Download PDF
                    </Button>
                    {writingAnswerId && !isLinkedWithInstitution && (
                      <Button
                        variant={
                          activeCommentSection ? "outlined" : "contained"
                        }
                        size="medium"
                        onClick={() =>
                          setActiveCommentSection(!activeCommentSection)
                        }
                        startIcon={
                          activeCommentSection ? <CloseIcon /> : <SendIcon />
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          px: 4,
                          py: 1,
                          borderRadius: 3,
                          minWidth: 180,
                          bgcolor: activeCommentSection
                            ? "transparent"
                            : BRAND_PRIMARY,
                          borderColor: BRAND_PRIMARY,
                          borderWidth: 2,
                          color: activeCommentSection ? BRAND_PRIMARY : "white",
                          "&:hover": {
                            bgcolor: activeCommentSection
                              ? BRAND_BG
                              : BRAND_DARK,
                            borderColor: BRAND_DARK,
                            borderWidth: 2,
                            transform: "translateY(-1px)",
                            boxShadow: `0 8px 25px rgba(157, 34, 53, 0.25)`,
                          },
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: activeCommentSection
                            ? `0 2px 8px rgba(157, 34, 53, 0.15)`
                            : `0 4px 14px rgba(157, 34, 53, 0.4)`,
                        }}
                      >
                        {activeCommentSection
                          ? "Exit Comment Mode"
                          : "Start Comment"}
                      </Button>
                    )}
                  </Box>

                  {/* Split Screen Layout when comments active */}
                  {activeCommentSection && writingAnswerId ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                        gap: 3,
                        minHeight: 700,
                      }}
                    >
                      {/* Left Side - PDF Viewer */}
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: "#f8fafc",
                          borderRadius: 3,
                          border: "2px solid #e2e8f0",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            background:
                              "linear-gradient(135deg, #334155 0%, #1e293b 100%)",
                            color: "white",
                            p: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: 18 }}>📄</Typography>
                            <Typography
                              sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                            >
                              Writing Feedback PDF
                            </Typography>
                          </Box>
                          <Chip
                            label="Live View"
                            size="small"
                            sx={{
                              bgcolor: "#475569",
                              color: "white",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                        <Box sx={{ height: 650, width: "100%" }}>
                          <iframe
                            src={`${evaluationPdf}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              background: "white",
                            }}
                            title="Writing Feedback PDF"
                          />
                        </Box>
                      </Paper>

                      {/* Right Side - Chat Interface */}
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: "white",
                          borderRadius: 3,
                          border: "1px solid #e2e8f0",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Chat Header */}
                        <Box
                          sx={{
                            background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`,
                            p: 2,
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: "rgba(255,255,255,0.2)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CommentIcon sx={{ color: "white" }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                sx={{
                                  color: "white",
                                  fontWeight: 700,
                                  fontSize: "1.125rem",
                                }}
                              >
                                Assessment Discussion
                              </Typography>
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.8)",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {commentsData?.comments?.length || 0} messages
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  bgcolor: "#4ade80",
                                  borderRadius: "50%",
                                  animation:
                                    "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                                  "@keyframes pulse": {
                                    "0%, 100%": { opacity: 1 },
                                    "50%": { opacity: 0.5 },
                                  },
                                }}
                              />
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.9)",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                }}
                              >
                                Active
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Messages Area */}
                        <Box
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            p: 2,
                            maxHeight: 500,
                            bgcolor: "#f8fafc",
                          }}
                        >
                          {commentsLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 8,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: "#64748b",
                                }}
                              >
                                <CircularProgress
                                  size={16}
                                  sx={{ color: BRAND_PRIMARY }}
                                />
                                <Typography sx={{ fontSize: "0.875rem" }}>
                                  Loading messages...
                                </Typography>
                              </Box>
                            </Box>
                          ) : commentsData?.comments?.length > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {commentsData.comments.map((comment, index) => (
                                <Box key={comment._id || index}>
                                  {/* Main Comment Message */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "start",
                                      gap: 1.5,
                                      justifyContent:
                                        comment.createdByModel === "student"
                                          ? "flex-start"
                                          : "flex-end",
                                    }}
                                  >
                                    {comment.createdByModel === "student" && (
                                      <Avatar
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`,
                                          boxShadow:
                                            "0 4px 12px rgba(157, 34, 53, 0.3)",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {comment.createdBy?.fullName
                                            ?.charAt(0)
                                            ?.toUpperCase() || "S"}
                                        </Typography>
                                      </Avatar>
                                    )}

                                    <Box
                                      sx={{
                                        maxWidth: "75%",
                                        order:
                                          comment.createdByModel === "student"
                                            ? 2
                                            : 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          borderRadius: 4,
                                          px: 2,
                                          py: 1.5,
                                          boxShadow:
                                            "0 2px 8px rgba(0,0,0,0.1)",
                                          background:
                                            comment.createdByModel === "student"
                                              ? `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`
                                              : "white",
                                          color:
                                            comment.createdByModel === "student"
                                              ? "white"
                                              : "#374151",
                                          borderBottomLeftRadius:
                                            comment.createdByModel === "student"
                                              ? 4
                                              : 16,
                                          borderBottomRightRadius:
                                            comment.createdByModel === "student"
                                              ? 16
                                              : 4,
                                          border:
                                            comment.createdByModel === "student"
                                              ? "none"
                                              : "2px solid #e2e8f0",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {comment.createdBy?.fullName ||
                                              (comment.createdByModel ===
                                              "student"
                                                ? "Student"
                                                : "Assessor")}
                                          </Typography>
                                          <Chip
                                            label={
                                              comment.createdByModel ===
                                              "student"
                                                ? "Student"
                                                : "Assessor"
                                            }
                                            size="small"
                                            sx={{
                                              height: 18,
                                              fontSize: "0.65rem",
                                              fontWeight: 600,
                                              bgcolor:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.2)"
                                                  : "#dcfce7",
                                              color:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.8)"
                                                  : "#16a34a",
                                            }}
                                          />
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontSize: "0.875rem",
                                            lineHeight: 1.6,
                                            whiteSpace: "pre-wrap",
                                          }}
                                        >
                                          {comment.commentText}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mt: 1.5,
                                            pt: 1,
                                            borderTop: `1px solid ${
                                              comment.createdByModel ===
                                              "student"
                                                ? "rgba(255,255,255,0.2)"
                                                : "#e2e8f0"
                                            }`,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "0.7rem",
                                              color:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.7)"
                                                  : "#64748b",
                                            }}
                                          >
                                            {new Date(
                                              comment.createdAt,
                                            ).toLocaleString("en-US", {
                                              month: "short",
                                              day: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <Button
                                              size="small"
                                              onClick={() => {
                                                setReplyingTo(
                                                  replyingTo === comment._id
                                                    ? null
                                                    : comment._id,
                                                );
                                                setReplyText("");
                                              }}
                                              sx={{
                                                fontSize: "0.7rem",
                                                fontWeight: 600,
                                                textTransform: "none",
                                                minWidth: "auto",
                                                p: 0.5,
                                                color:
                                                  comment.createdByModel ===
                                                  "student"
                                                    ? "rgba(255,255,255,0.8)"
                                                    : BRAND_PRIMARY,
                                                "&:hover": {
                                                  color:
                                                    comment.createdByModel ===
                                                    "student"
                                                      ? "white"
                                                      : BRAND_DARK,
                                                },
                                              }}
                                            >
                                              Reply
                                            </Button>
                                            {comment.createdByModel ===
                                              "student" && (
                                              <Button
                                                size="small"
                                                onClick={() =>
                                                  handleDeleteComment(
                                                    comment._id,
                                                  )
                                                }
                                                sx={{
                                                  fontSize: "0.7rem",
                                                  fontWeight: 600,
                                                  textTransform: "none",
                                                  minWidth: "auto",
                                                  p: 0.5,
                                                  color: "#fca5a5",
                                                  "&:hover": {
                                                    color: "#ef4444",
                                                  },
                                                }}
                                              >
                                                Delete
                                              </Button>
                                            )}
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {comment.createdByModel !== "student" && (
                                      <Avatar
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          background:
                                            "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                          boxShadow:
                                            "0 4px 12px rgba(16, 185, 129, 0.3)",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {comment.createdBy?.fullName
                                            ?.charAt(0)
                                            ?.toUpperCase() || "A"}
                                        </Typography>
                                      </Avatar>
                                    )}
                                  </Box>

                                  {/* Reply Input */}
                                  {replyingTo === comment._id && (
                                    <Box
                                      sx={{
                                        ml: 6,
                                        mt: 1.5,
                                        bgcolor: "white",
                                        borderRadius: 2,
                                        p: 2,
                                        border: "1px solid #e2e8f0",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          mb: 1,
                                        }}
                                      >
                                        <ReplyIcon
                                          sx={{
                                            color: BRAND_PRIMARY,
                                            fontSize: 16,
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: "0.875rem",
                                            color: "#64748b",
                                            fontWeight: 600,
                                          }}
                                        >
                                          Replying to comment
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setReplyingTo(null);
                                            setReplyText("");
                                          }}
                                          sx={{ ml: "auto", p: 0.5 }}
                                        >
                                          <CloseIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "#64748b",
                                            }}
                                          />
                                        </IconButton>
                                      </Box>
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          value={replyText}
                                          onChange={(e) =>
                                            setReplyText(e.target.value)
                                          }
                                          placeholder="Write a reply..."
                                          onKeyPress={(e) => {
                                            if (
                                              e.key === "Enter" &&
                                              !e.shiftKey
                                            ) {
                                              e.preventDefault();
                                              handleSubmitReply(comment._id);
                                            }
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 2,
                                            },
                                          }}
                                        />
                                        <Button
                                          variant="contained"
                                          onClick={() =>
                                            handleSubmitReply(comment._id)
                                          }
                                          disabled={
                                            !replyText.trim() || isReplying
                                          }
                                          sx={{
                                            bgcolor: BRAND_PRIMARY,
                                            "&:hover": { bgcolor: BRAND_DARK },
                                            minWidth: "auto",
                                            px: 2,
                                          }}
                                        >
                                          {isReplying ? (
                                            <CircularProgress
                                              size={16}
                                              color="inherit"
                                            />
                                          ) : (
                                            "Send"
                                          )}
                                        </Button>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Replies */}
                                  {comment.replies &&
                                    comment.replies.length > 0 && (
                                      <Box
                                        sx={{
                                          ml: 6,
                                          mt: 1,
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1,
                                        }}
                                      >
                                        {comment.replies.map(
                                          (reply, replyIndex) => (
                                            <Box
                                              key={reply._id || replyIndex}
                                              sx={{
                                                display: "flex",
                                                alignItems: "start",
                                                gap: 1,
                                                justifyContent:
                                                  reply.repliedByModel ===
                                                  "student"
                                                    ? "flex-start"
                                                    : "flex-end",
                                              }}
                                            >
                                              {reply.repliedByModel ===
                                                "student" && (
                                                <Avatar
                                                  sx={{
                                                    width: 32,
                                                    height: 32,
                                                    background: `linear-gradient(135deg, ${BRAND_PRIMARY}CC 0%, ${BRAND_DARK}CC 100%)`,
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontWeight: 700,
                                                      fontSize: "0.75rem",
                                                    }}
                                                  >
                                                    {reply.repliedBy?.fullName
                                                      ?.charAt(0)
                                                      ?.toUpperCase() || "S"}
                                                  </Typography>
                                                </Avatar>
                                              )}

                                              <Box
                                                sx={{
                                                  maxWidth: "70%",
                                                  borderRadius: 3,
                                                  px: 1.5,
                                                  py: 1,
                                                  boxShadow:
                                                    "0 1px 4px rgba(0,0,0,0.08)",
                                                  background:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? `linear-gradient(135deg, ${BRAND_PRIMARY}E6 0%, ${BRAND_DARK}E6 100%)`
                                                      : "#f1f5f9",
                                                  color:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? "white"
                                                      : "#374151",
                                                  borderBottomLeftRadius:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? 2
                                                      : 12,
                                                  borderBottomRightRadius:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? 12
                                                      : 2,
                                                  border:
                                                    reply.repliedByModel ===
                                                    "student"
                                                      ? "none"
                                                      : "1px solid #e2e8f0",
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    mb: 0.5,
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: "0.75rem",
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    {reply.repliedBy
                                                      ?.fullName ||
                                                      (reply.repliedByModel ===
                                                      "student"
                                                        ? "Student"
                                                        : "Assessor")}
                                                  </Typography>
                                                </Box>
                                                <Typography
                                                  sx={{
                                                    fontSize: "0.75rem",
                                                    lineHeight: 1.5,
                                                    whiteSpace: "pre-wrap",
                                                  }}
                                                >
                                                  {reply.commentText}
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                      "space-between",
                                                    mt: 0.5,
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: "0.65rem",
                                                      color:
                                                        reply.repliedByModel ===
                                                        "student"
                                                          ? "rgba(255,255,255,0.7)"
                                                          : "#64748b",
                                                    }}
                                                  >
                                                    {new Date(
                                                      reply.createdAt,
                                                    ).toLocaleString("en-US", {
                                                      month: "short",
                                                      day: "numeric",
                                                      hour: "2-digit",
                                                      minute: "2-digit",
                                                    })}
                                                  </Typography>
                                                  {reply.repliedByModel ===
                                                    "student" && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                        handleDeleteReply(
                                                          comment._id,
                                                          reply._id,
                                                        )
                                                      }
                                                      sx={{
                                                        p: 0.25,
                                                        color: "#fca5a5",
                                                        "&:hover": {
                                                          color: "#ef4444",
                                                        },
                                                      }}
                                                    >
                                                      <DeleteIcon
                                                        sx={{ fontSize: 14 }}
                                                      />
                                                    </IconButton>
                                                  )}
                                                </Box>
                                              </Box>

                                              {reply.repliedByModel !==
                                                "student" && (
                                                <Avatar
                                                  sx={{
                                                    width: 32,
                                                    height: 32,
                                                    background:
                                                      "linear-gradient(135deg, #10b981CC 0%, #059669CC 100%)",
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontWeight: 700,
                                                      fontSize: "0.75rem",
                                                    }}
                                                  >
                                                    {reply.repliedBy?.fullName
                                                      ?.charAt(0)
                                                      ?.toUpperCase() || "A"}
                                                  </Typography>
                                                </Avatar>
                                              )}
                                            </Box>
                                          ),
                                        )}
                                      </Box>
                                    )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 8,
                                textAlign: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  bgcolor: "#f1f5f9",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mb: 2,
                                }}
                              >
                                <CommentIcon
                                  sx={{ fontSize: 32, color: "#cbd5e1" }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: "#64748b",
                                  mb: 0.5,
                                }}
                              >
                                No messages yet
                              </Typography>
                              <Typography
                                sx={{ fontSize: "0.875rem", color: "#94a3b8" }}
                              >
                                Start a conversation with your assessor
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Message Input */}
                        <Box
                          sx={{
                            p: 2,
                            borderTop: "1px solid #e2e8f0",
                            bgcolor: "white",
                            borderBottomLeftRadius: 12,
                            borderBottomRightRadius: 12,
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 1.5 }}>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              maxRows={3}
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Type your message..."
                              onKeyPress={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSubmitComment();
                                }
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 3,
                                  bgcolor: "#f8fafc",
                                },
                              }}
                            />
                            <Button
                              variant="contained"
                              onClick={handleSubmitComment}
                              disabled={!newComment.trim() || isCreating}
                              sx={{
                                bgcolor: BRAND_PRIMARY,
                                "&:hover": { bgcolor: BRAND_DARK },
                                minWidth: "auto",
                                px: 3,
                                borderRadius: 3,
                              }}
                            >
                              {isCreating ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <SendIcon />
                              )}
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  ) : (
                    /* Normal PDF View */
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 600,
                          position: "relative",
                          bgcolor: "#ffffff",
                        }}
                      >
                        <iframe
                          src={`${evaluationPdf}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                          }}
                          title="Writing Feedback PDF"
                        />
                        {/* Fallback content */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f8fafc",
                            zIndex: -1,
                          }}
                        >
                          <Box sx={{ textAlign: "center", p: 3 }}>
                            <PdfIcon
                              sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }}
                            />
                            <Typography sx={{ color: "#64748b", mb: 1.5 }}>
                              PDF preview not available in your browser
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#94a3b8" }}
                            >
                              Click "Download PDF" button above to open the PDF
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}

              {!adminComments && !evaluationPdf && (
                <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                  Your writing has been evaluated. Score and grade are shown
                  above.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <ScheduleIcon sx={{ fontSize: 40, color: BRAND_LIGHT, mb: 1 }} />
              <Typography
                sx={{ fontWeight: 600, color: BRAND_PRIMARY, mb: 0.5 }}
              >
                Evaluation in Progress
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                Your writing will be evaluated within 24 hours.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.text}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeleteItem(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#1e293b",
            fontSize: "1.125rem",
            pb: 1,
          }}
        >
          {deleteItem?.type === "comment" ? "Delete Comment?" : "Delete Reply?"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#64748b", mb: 2 }}>
            {deleteItem?.type === "comment"
              ? "This comment and all its replies will be permanently deleted. This action cannot be undone."
              : "This reply will be permanently deleted. This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteItem(null);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#64748b",
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: "#ef4444",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#dc2626" },
            }}
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ---------------------------------------------------------------
   Main WritingSectionReview Component
--------------------------------------------------------------- */
export default function WritingSectionReview({
  writingData = {},
  submissionData = {},
  // Notes props
  notes = [],
  onAddNote = null,
  notesModalOpen = false,
  setNotesModalOpen = null,
}) {
  // Extract data from props - API returns 'sections' which we display as case notes
  // writingData.test.sections OR writingData.sections OR writingData.caseNotes
  const caseNotes =
    writingData.sections ||
    writingData.caseNotes ||
    writingData.test?.sections ||
    [];
  const writingPrompt =
    writingData.writingPrompt || writingData.test?.writingPrompt || "";
  const instructions =
    writingData.instructions || writingData.test?.instructions || "";
  const testTitle = writingData.title || writingData.test?.title || "";

  // Get writing tasks from both writingData and test
  const writingTasks =
    writingData.writingTasks || writingData.test?.tasks || [];
  const submissionTasks = submissionData.writingTasks || [];

  // Submission data (aggregated for backward compatibility)
  const submittedText = submissionData.writingText || "";
  const submittedImages = submissionData.writingImage || [];
  const isPaperBased =
    submissionData.isPictureBased || submittedImages.length > 0;
  // Handle nested pdfUrl structure: can be string or object with pdfUrl property
  const evaluationPdf =
    typeof submissionData.pdfUrl === "string"
      ? submissionData.pdfUrl
      : submissionData.pdfUrl?.pdfUrl ||
        submissionData.evaluationPdf ||
        submissionData.accessorFeedBackInPdf?.pdfUrl ||
        "";
  const marks = submissionData.marks ?? submissionData.score;
  const grade = submissionData.grade || "";
  const adminComments =
    submissionData.adminComments || submissionData.accessorFeedBack || "";
  const createdAt = submissionData.createdAt || "";
  const checkingStatus = submissionData.checkingStatus || "";
  const isEvaluated =
    ((marks !== null && marks !== undefined && marks > 0) || !!evaluationPdf) &&
    !checkingStatus.toLowerCase().includes("pending");
  const evaluatorName = submissionData.evaluatorId?.fullName || "";

  // Get writingAnswerId for comments API - try multiple possible sources
  const writingAnswerId =
    submissionData._id ||
    submissionData.id ||
    submissionData.writingAnswerId ||
    submissionData.writingResultId ||
    (submissionTasks.length > 0 ? submissionTasks[0]._id : "") ||
    "";

  // Debug logging for troubleshooting
  console.log("WritingSectionReview - submissionData:", submissionData);
  console.log("WritingSectionReview - writingAnswerId:", writingAnswerId);
  console.log("WritingSectionReview - evaluationPdf:", evaluationPdf);

  // Detect if this is IELTS (has multiple tasks or band scores 1-9)
  const isIelts =
    submissionTasks.length > 1 ||
    writingTasks.length > 1 ||
    (marks !== null && marks !== undefined && marks >= 1 && marks <= 9);

  // Determine if we should show multi-task view (IELTS with multiple tasks)
  const hasMultipleTasks =
    submissionTasks.length > 1 || writingTasks.length > 1;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Card
        elevation={0}
        sx={{
          mb: 0,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "#fff",
          borderBottom: "1px solid #e2e8f0",
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left - Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: BRAND_PRIMARY,
                width: 40,
                height: 40,
              }}
            >
              <CreateIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: BRAND_PRIMARY,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Writing Module Review
              </Typography>
              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {testTitle ||
                  (isPaperBased ? "Paper-Based Test" : "Computer-Based Test")}
              </Typography>
            </Box>
            {/* Notes Button */}
            {setNotesModalOpen && (
              <Tooltip title="View Notes" placement="left" arrow>
                <IconButton
                  onClick={() => setNotesModalOpen(true)}
                  sx={{
                    bgcolor: BRAND_PRIMARY,
                    color: "#fff",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      bgcolor: BRAND_DARK,
                    },
                  }}
                >
                  <Badge
                    badgeContent={notes?.length || 0}
                    color="error"
                    overlap="circular"
                  >
                    <StickyNote2Icon sx={{ fontSize: 18 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Card>

      {/* Split View - Case Notes & Writing Task */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: { xs: "auto", md: "calc(100vh - 80px)" },
          overflow: "hidden",
        }}
      >
        {/* Left Panel - Writing Tasks/Instructions from Test */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: 1, sm: 2 },
            borderRight: { md: "2px solid #e0e0e0" },
            borderBottom: { xs: "2px solid #e0e0e0", md: "none" },
            background: "#fff",
          }}
          className="custom-scrollbar"
        >
          {/* Show writing tasks if available, otherwise show case notes */}
          {writingTasks.length > 0 ? (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  color: "#1e293b",
                }}
              >
                Writing Tasks
              </Typography>
              {writingTasks.map((task, idx) => (
                <Card
                  key={task._id || idx}
                  elevation={1}
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 2,
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      mb: { xs: 1, sm: 1.5 },
                      color: BRAND_PRIMARY,
                      fontSize: { xs: "1.05rem", sm: "1.15rem" },
                    }}
                  >
                    {task.taskTitle || `Task ${task.taskNumber || idx + 1}`}
                  </Typography>
                  {task.instructions && (
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 2,
                        bgcolor: "#f0f9ff",
                        borderRadius: 1,
                        borderLeft: "3px solid #3b82f6",
                      }}
                    >
                      <TextHighlighterWrapper onAddNote={onAddNote}>
                        <Typography
                          sx={{
                            color: "#1e40af",
                            fontSize: { xs: "0.85rem", sm: "0.9rem" },
                            fontWeight: 500,
                          }}
                        >
                          {task.instructions}
                        </Typography>
                      </TextHighlighterWrapper>
                    </Box>
                  )}
                  {task.task && (
                    <Box>
                      <TextHighlighterWrapper onAddNote={onAddNote}>
                        <Typography
                          sx={{
                            color: "#1e293b",
                            fontSize: { xs: "0.875rem", sm: "0.95rem" },
                            lineHeight: 1.7,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {task.task}
                        </Typography>
                      </TextHighlighterWrapper>
                    </Box>
                  )}
                  {task.imageURL && (
                    <Box sx={{ mt: 2 }}>
                      <img
                        src={task.imageURL}
                        alt={`Task ${task.taskNumber || idx + 1} reference`}
                        style={{
                          width: "100%",
                          maxWidth: "600px",
                          height: "auto",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      />
                    </Box>
                  )}
                </Card>
              ))}
            </Box>
          ) : (
            <CaseNotesSection caseNotes={caseNotes} onAddNote={onAddNote} />
          )}
        </Box>

        {/* Right Panel - Writing Task Submissions */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: 1, sm: 2 },
            background: "#f8f9fa",
          }}
          className="custom-scrollbar"
        >
          {/* Show individual task submissions if available */}
          {hasMultipleTasks && submissionTasks.length > 0 ? (
            <Box sx={{ py: { xs: 1, sm: 2 } }}>
              {submissionTasks.map((task, idx) => {
                const taskImages = task.writingImage || [];
                const taskText = task.writingText || "";
                const taskCheckingStatus = task.checkingStatus || "";
                const taskScore = task.score;
                const taskGrade = task.grade;
                
                // Try to match submission task to test task to get correct taskNumber
                let displayTaskNumber = task.taskNumber;
                if (!displayTaskNumber && writingTasks.length > 0) {
                  // Try to match by taskId if available
                  if (task.taskId) {
                    const matchedTestTask = writingTasks.find(t => t._id === task.taskId);
                    displayTaskNumber = matchedTestTask?.taskNumber;
                  }
                  // If still no match and only 1 submission but multiple test tasks,
                  // try to infer which task this submission is for
                  if (!displayTaskNumber && submissionTasks.length === 1 && writingTasks.length > 1) {
                    // Default to the last test task (usually Task 2 in IELTS)
                    displayTaskNumber = writingTasks[writingTasks.length - 1]?.taskNumber || idx + 1;
                  }
                }
                // Final fallback
                displayTaskNumber = displayTaskNumber || idx + 1;
                
                const taskEvaluated =
                  ((taskScore !== null &&
                    taskScore !== undefined &&
                    taskScore > 0) ||
                    task.accessorFeedBackInPdf?.pdfUrl) &&
                  !taskCheckingStatus.toLowerCase().includes("pending");
                const taskEvaluatorName =
                  task.evaluatorId?.fullName || evaluatorName;

                return (
                  <Card
                    key={task._id || idx}
                    elevation={0}
                    sx={{
                      mb: 3,
                      border: "1px solid #e2e8f0",
                      borderRadius: 2,
                    }}
                  >
                    {/* Task Header */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: BRAND_BG,
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        {taskImages.length > 0 ? (
                          <ImageIcon sx={{ color: BRAND_PRIMARY }} />
                        ) : (
                          <CreateIcon sx={{ color: BRAND_PRIMARY }} />
                        )}
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#1e293b",
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                          }}
                        >
                          Task {displayTaskNumber} -{" "}
                          {task.taskType === "letter"
                            ? "Letter"
                            : task.taskType === "essay"
                              ? "Essay"
                              : "Response"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {taskEvaluated && taskGrade && taskGrade > 0 && (
                          <Chip
                            icon={<GradeIcon sx={{ fontSize: 14 }} />}
                            label={`Band ${taskGrade}`}
                            size="small"
                            sx={{
                              bgcolor: `${getIeltsBandInfo(taskGrade)?.color || BRAND_PRIMARY}20`,
                              color:
                                getIeltsBandInfo(taskGrade)?.color ||
                                BRAND_PRIMARY,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                            }}
                          />
                        )}
                        <Chip
                          icon={
                            taskEvaluated ? (
                              <CheckCircleIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ScheduleIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          label={taskEvaluated ? "Evaluated" : "Pending"}
                          size="small"
                          sx={{
                            bgcolor: taskEvaluated ? SUCCESS_BG : BRAND_BG,
                            color: taskEvaluated
                              ? SUCCESS_GREEN
                              : BRAND_PRIMARY,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Task Submission Content */}
                    <CardContent>
                      {taskImages.length > 0 ? (
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              mb: 2,
                              color: "#64748b",
                              fontSize: "0.875rem",
                            }}
                          >
                            Uploaded Images ({taskImages.length})
                          </Typography>
                          <Grid container spacing={2}>
                            {taskImages.map((image, imgIdx) => (
                              <Grid item xs={12} sm={6} key={imgIdx}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      transform: "scale(1.02)",
                                      boxShadow: 3,
                                    },
                                  }}
                                  onClick={() =>
                                    window.open(image.imageUrl, "_blank")
                                  }
                                >
                                  <Box sx={{ position: "relative" }}>
                                    <img
                                      src={image.imageUrl}
                                      alt={`Task ${displayTaskNumber} submission ${imgIdx + 1}`}
                                      style={{
                                        width: "100%",
                                        height: 180,
                                        objectFit: "cover",
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        bgcolor: "rgba(0,0,0,0.6)",
                                        borderRadius: 1,
                                        p: 0.5,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <ZoomInIcon
                                        sx={{ color: "#fff", fontSize: 14 }}
                                      />
                                      <Typography
                                        sx={{ color: "#fff", fontSize: 10 }}
                                      >
                                        View
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {image.originalName && (
                                    <Box sx={{ p: 1, bgcolor: "#f8fafc" }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                      >
                                        {image.originalName}
                                      </Typography>
                                    </Box>
                                  )}
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ) : taskText ? (
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#64748b",
                                fontSize: "0.875rem",
                              }}
                            >
                              Written Response
                            </Typography>
                            <Chip
                              label={`${taskText.trim().split(/\s+/).filter(Boolean).length} words`}
                              size="small"
                              sx={{
                                bgcolor: BRAND_BG,
                                color: BRAND_PRIMARY,
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: "#fff",
                              borderRadius: 2,
                              minHeight: 100,
                            }}
                          >
                            <TextHighlighterWrapper onAddNote={onAddNote}>
                              <Typography
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  lineHeight: 1.7,
                                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                                  color: "#374151",
                                }}
                              >
                                {taskText}
                              </Typography>
                            </TextHighlighterWrapper>
                          </Paper>
                        </Box>
                      ) : (
                        <Box
                          sx={{ textAlign: "center", py: 3, color: "#64748b" }}
                        >
                          <DescriptionIcon
                            sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }}
                          />
                          <Typography variant="body2">
                            No submission for this task
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {/* Overall Evaluation Card */}
              <WritingEvaluationSection
                evaluationPdf={evaluationPdf}
                marks={marks}
                grade={grade}
                adminComments={adminComments}
                isEvaluated={isEvaluated}
                checkingStatus={checkingStatus}
                writingAnswerId={writingAnswerId}
                isIelts={isIelts}
              />
            </Box>
          ) : (
            <WritingTaskSection
              writingPrompt={writingPrompt}
              instructions={instructions}
              submittedText={submittedText}
              submittedImages={submittedImages}
              isPaperBased={isPaperBased}
              evaluationPdf={evaluationPdf}
              marks={marks}
              grade={grade}
              adminComments={adminComments}
              createdAt={createdAt}
              isEvaluated={isEvaluated}
              checkingStatus={checkingStatus}
              writingAnswerId={writingAnswerId}
              isIelts={isIelts}
              onAddNote={onAddNote}
              taskNumber={writingTasks?.taskNumber}
            />
          )}
        </Box>
      </Box>

      {/* Notes Modal */}
      {setNotesModalOpen && (
        <NotesModal
          open={notesModalOpen}
          onClose={() => setNotesModalOpen(false)}
          notes={notes}
        />
      )}
    </Box>
  );
}
